/**
 * ProjectsPanel - Right-side panel for managing Taurus projects
 *
 * Displays list of saved projects, sample projects, and controls for
 * creating/editing/running projects.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  X,
  Search,
  Plus,
  Play,
  Edit,
  Copy,
  Trash2,
  FileJson,
  Layers,
  GitBranch,
  Clock,
  ChevronRight,
  Upload,
  Download,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Zap,
  RotateCcw,
  Pause,
  BookOpen
} from 'lucide-react';
import { SAMPLE_PROJECTS, formatProjectJson } from '../config/projectSchema';

export default function ProjectsPanel({
  isOpen,
  onClose,
  // Project actions
  onNewProject,
  onEditProject,
  onRunProject,
  onDeleteProject,
  // Saved projects (from localStorage for now)
  savedProjects = [],
  // Currently running project
  runningProject = null,
  // Tier gating
  userTier = 'free',
  onUpgrade
}) {
  const isPro = userTier === 'pro' || userTier === 'team' || userTier === 'admin' || userTier === 'owner';
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    running: true,
    saved: true,
    samples: true
  });

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Filter projects based on search
  const filteredSavedProjects = useMemo(() => {
    if (!searchQuery.trim()) return savedProjects;
    const query = searchQuery.toLowerCase();
    return savedProjects.filter(p =>
      p.name.toLowerCase().includes(query) ||
      p.description?.toLowerCase().includes(query) ||
      p.tags?.some(t => t.toLowerCase().includes(query))
    );
  }, [savedProjects, searchQuery]);

  const filteredSampleProjects = useMemo(() => {
    if (!searchQuery.trim()) return SAMPLE_PROJECTS;
    const query = searchQuery.toLowerCase();
    return SAMPLE_PROJECTS.filter(p =>
      p.name.toLowerCase().includes(query) ||
      p.description?.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Copy sample to create new project (Pro only)
  const handleCopySample = (sample) => {
    if (!isPro) { onUpgrade?.(); return; }
    onEditProject({
      ...sample,
      id: null, // Will be assigned on save
      name: sample.json.project_name,
      isSample: false,
      isNew: true
    });
  };

  // Export project as JSON file
  const handleExport = (project) => {
    const json = formatProjectJson(project.json);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Import project from JSON file (Pro only)
  const handleImport = () => {
    if (!isPro) { onUpgrade?.(); return; }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      try {
        const text = await file.text();
        const json = JSON.parse(text);
        onEditProject({
          id: null,
          name: json.project_name || file.name.replace('.json', ''),
          json,
          isNew: true,
          isImported: true
        });
      } catch (err) {
        console.error('Failed to import project:', err);
        alert('Failed to import project. Please check the JSON format.');
      }
    };
    input.click();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 dark:bg-black/50 z-30"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-96 bg-white dark:bg-gray-900 shadow-2xl z-40 flex flex-col border-l border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Projects</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Actions */}
        <div className="flex gap-2 p-4 border-b border-gray-200 dark:border-gray-700">
          {isPro ? (
            <>
              <button
                onClick={onNewProject}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
              >
                <Plus className="h-4 w-4" />
                New Project
              </button>
              <button
                onClick={handleImport}
                className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg transition-colors text-sm"
                title="Import JSON"
              >
                <Upload className="h-4 w-4" />
              </button>
            </>
          ) : (
            <button
              onClick={onUpgrade}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
            >
              <Zap className="h-4 w-4" />
              Upgrade to Pro to create projects
            </button>
          )}
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Running Project */}
          {runningProject && (
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => toggleSection('running')}
                className="flex items-center justify-between w-full text-left mb-2"
              >
                <span className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wider">
                  Running
                </span>
                <ChevronRight
                  className={`h-4 w-4 text-gray-400 transition-transform ${
                    expandedSections.running ? 'rotate-90' : ''
                  }`}
                />
              </button>
              {expandedSections.running && (
                <ProjectCard
                  project={runningProject}
                  isRunning={true}
                  onView={() => {/* scroll to execution in chat */}}
                />
              )}
            </div>
          )}

          {/* Saved Projects */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => toggleSection('saved')}
              className="flex items-center justify-between w-full text-left mb-2"
            >
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                My Projects ({filteredSavedProjects.length})
              </span>
              <ChevronRight
                className={`h-4 w-4 text-gray-400 transition-transform ${
                  expandedSections.saved ? 'rotate-90' : ''
                }`}
              />
            </button>
            {expandedSections.saved && (
              <div className="space-y-2">
                {filteredSavedProjects.length === 0 ? (
                  searchQuery ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic py-2">
                      No projects match your search
                    </p>
                  ) : !isPro ? (
                    <div className="py-3 text-center">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Create and save your own workflows with <span className="font-medium text-blue-500 cursor-pointer hover:underline" onClick={onUpgrade}>Pro</span>.
                      </p>
                    </div>
                  ) : (
                    <EmptyState onNewProject={onNewProject} />
                  )
                ) : (
                  filteredSavedProjects.map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      onEdit={() => onEditProject(project)}
                      onRun={() => onRunProject(project)}
                      onDelete={() => onDeleteProject(project.id)}
                      onExport={() => handleExport(project)}
                    />
                  ))
                )}
              </div>
            )}
          </div>

          {/* Sample Projects */}
          <div className="p-4">
            <button
              onClick={() => toggleSection('samples')}
              className="flex items-center justify-between w-full text-left mb-2"
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Sample Templates
                </span>
                <Sparkles className="h-3 w-3 text-yellow-500" />
              </div>
              <ChevronRight
                className={`h-4 w-4 text-gray-400 transition-transform ${
                  expandedSections.samples ? 'rotate-90' : ''
                }`}
              />
            </button>
            {expandedSections.samples && (
              <div className="space-y-2">
                {filteredSampleProjects.map((sample) => (
                  <ProjectCard
                    key={sample.id}
                    project={{
                      ...sample,
                      name: sample.name,
                      stepCount: sample.json.steps?.length || 0,
                      features: sample.features || []
                    }}
                    isSample={true}
                    onCopy={() => handleCopySample(sample)}
                    onRun={() => onRunProject(sample)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// Project Card Component
function ProjectCard({
  project,
  isRunning = false,
  isSample = false,
  onEdit,
  onRun,
  onDelete,
  onExport,
  onCopy,
  onView
}) {
  const stepCount = project.stepCount || project.json?.steps?.length || 0;

  return (
    <div className={`
      p-3 rounded-lg border transition-colors
      ${isRunning
        ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
        : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 hover:border-gray-300 dark:hover:border-gray-600'
      }
    `}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <FileJson className={`h-4 w-4 flex-shrink-0 ${
              isRunning ? 'text-green-600 dark:text-green-400' : 'text-blue-500'
            }`} />
            <h3 className="font-medium text-sm text-gray-900 dark:text-white truncate">
              {project.name}
            </h3>
          </div>
          {project.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
              {project.description}
            </p>
          )}
          <div className="flex items-center gap-3 mt-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {stepCount} step{stepCount !== 1 ? 's' : ''}
            </span>
            {project.updatedAt && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <Clock className="h-3 w-3" />
                {new Date(project.updatedAt).toLocaleDateString()}
              </span>
            )}
          </div>
          {/* Feature tags for samples */}
          {project.features?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {project.features.map((feature, idx) => (
                <span
                  key={idx}
                  className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded"
                >
                  {feature}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
        {isRunning ? (
          <button
            onClick={onView}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 rounded transition-colors"
          >
            View Progress
          </button>
        ) : isSample ? (
          <>
            <button
              onClick={onCopy}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              title="Edit as new project"
            >
              <Edit className="h-3 w-3" />
              Edit
            </button>
            <button
              onClick={onRun}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded transition-colors"
            >
              <Play className="h-3 w-3" />
              Run
            </button>
          </>
        ) : (
          <>
            <button
              onClick={onEdit}
              className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              title="Edit"
            >
              <Edit className="h-3 w-3" />
            </button>
            <button
              onClick={onRun}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded transition-colors"
            >
              <Play className="h-3 w-3" />
              Run
            </button>
            <button
              onClick={onExport}
              className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              title="Export JSON"
            >
              <Download className="h-3 w-3" />
            </button>
            <button
              onClick={onDelete}
              className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
              title="Delete"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Empty State Component - shown when no projects exist
function EmptyState({ onNewProject }) {
  return (
    <div className="py-4 px-2">
      {/* Visual step flow */}
      <div className="flex items-center justify-center gap-1 mb-4">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
          <span className="text-xs font-bold">1</span>
        </div>
        <ArrowRight className="h-3 w-3 text-gray-400" />
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
          <span className="text-xs font-bold">2</span>
        </div>
        <ArrowRight className="h-3 w-3 text-gray-400" />
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
          <span className="text-xs font-bold">3</span>
        </div>
        <ArrowRight className="h-3 w-3 text-gray-400" />
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
          <CheckCircle2 className="h-4 w-4" />
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 dark:text-gray-300 text-center mb-4">
        Automate multi-step AI workflows with JSON
      </p>

      {/* Feature list */}
      <div className="space-y-2 mb-4">
        <FeatureItem icon={GitBranch} text="Chain steps with shared context" />
        <FeatureItem icon={Layers} text="Run independent tasks in batch" />
        <FeatureItem icon={Pause} text="Control review points (auto/manual)" />
        <FeatureItem icon={RotateCcw} text="Reset or continue REPL state" />
        <FeatureItem icon={Zap} text="Rate limit with delays" />
      </div>

      {/* Tip */}
      <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg mb-4">
        <BookOpen className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700 dark:text-amber-300">
          <span className="font-medium">Tip:</span> Try a sample template below to see how projects work, then create your own.
        </p>
      </div>
    </div>
  );
}

// Feature item for empty state
function FeatureItem({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 text-gray-400" />
      <span className="text-xs text-gray-600 dark:text-gray-400">{text}</span>
    </div>
  );
}
